import {
  LICENSE_CLASS_OPTIONS,
  TRIP_REQUEST_CARE_TYPE_OPTIONS,
  TripRequestCareTypeEnum,
  VEHICLE_TYPE_OPTIONS,
  VehicleBodyTypeEnum
} from '@/types/enums'

export {
  LICENSE_CLASS_OPTIONS,
  TRIP_REQUEST_CARE_TYPE_OPTIONS,
  VEHICLE_TYPE_OPTIONS
}

// Simple vehicle type utilities
export class VehicleUtils {
  /**
   * Get license class name by value
   */
  static getLicenseClassName(value: string): string {
    const option = LICENSE_CLASS_OPTIONS.find((opt) => opt.value === value)
    return option?.label || 'Unknown'
  }

  /**
   * Get license class value by name
   */
  static getLicenseClassValue(name: string): string | undefined {
    const option = LICENSE_CLASS_OPTIONS.find(
      (opt) => opt.label.toLowerCase() === name.toLowerCase()
    )
    return option?.value
  }

  /**
   * Get vehicle type name by value
   */
  static getVehicleTypeName(value: number): string {
    const option = VEHICLE_TYPE_OPTIONS.find(
      (opt) => opt.value === value.toString()
    )
    return option?.label || 'Unknown'
  }

  /**
   * Get vehicle type value by name
   */
  static getVehicleTypeValue(name: string): number | undefined {
    const option = VEHICLE_TYPE_OPTIONS.find(
      (opt) => opt.label.toLowerCase() === name.toLowerCase()
    )
    return option?.value ? parseInt(option.value) : undefined
  }
}

// Export utility functions for easy use
export const {
  getLicenseClassName,
  getLicenseClassValue,
  getVehicleTypeName,
  getVehicleTypeValue
} = VehicleUtils

export const getVehicleBodyTypeInfo = (
  bodyType: VehicleBodyTypeEnum
): { image: string; name: string } => {
  switch (bodyType) {
    case VehicleBodyTypeEnum.BUS:
      return { image: '/media/cars/bus.png', name: 'Bus' }
    case VehicleBodyTypeEnum.HATCHBACK:
      return { image: '/media/cars/hatchback.png', name: 'Hatchback' }
    case VehicleBodyTypeEnum.MICROBUS:
      return { image: '/media/cars/microbus.png', name: 'Microbus' }
    case VehicleBodyTypeEnum.MINIBUS:
      return { image: '/media/cars/minibus.png', name: 'Minibus' }
    case VehicleBodyTypeEnum.MINIVAN:
      return { image: '/media/cars/minivan.png', name: 'Minivan' }
    case VehicleBodyTypeEnum.SEDAN:
      return { image: '/media/cars/sedan.png', name: 'Sedan' }
    case VehicleBodyTypeEnum.SUV:
      return { image: '/media/cars/suv.png', name: 'SUV' }
    case VehicleBodyTypeEnum.VAN:
      return { image: '/media/cars/van.png', name: 'Van' }
    default:
      return { image: '/media/cars/sedan.png', name: 'Vehicle' }
  }
}

export const getTripRequestCareTypeInfo = (
  careType: TripRequestCareTypeEnum
): { image: string; name: string } => {
  switch (careType) {
    case TripRequestCareTypeEnum.BUS:
      return { image: '/media/cars/bus.png', name: 'Bus' }
    case TripRequestCareTypeEnum.CAR:
      return { image: '/media/cars/sedan.png', name: 'Car' }
    case TripRequestCareTypeEnum.MICROBUS:
      return { image: '/media/cars/microbus.png', name: 'Microbus' }
    case TripRequestCareTypeEnum.MINIBUS:
      return { image: '/media/cars/minibus.png', name: 'Minibus' }
    default:
      return { image: '/media/cars/sedan.png', name: 'Vehicle' }
  }
}

const TRIP_REQUEST_CARE_TYPE_TO_VEHICLE_BODY_TYPES: Record<
  TripRequestCareTypeEnum,
  VehicleBodyTypeEnum[]
> = {
  [TripRequestCareTypeEnum.BUS]: [VehicleBodyTypeEnum.BUS],
  [TripRequestCareTypeEnum.CAR]: [
    VehicleBodyTypeEnum.SEDAN,
    VehicleBodyTypeEnum.HATCHBACK,
    VehicleBodyTypeEnum.SUV,
    VehicleBodyTypeEnum.VAN,
    VehicleBodyTypeEnum.MINIVAN
  ],
  [TripRequestCareTypeEnum.MICROBUS]: [VehicleBodyTypeEnum.MICROBUS],
  [TripRequestCareTypeEnum.MINIBUS]: [VehicleBodyTypeEnum.MINIBUS]
}

export const getMatchingVehicleBodyTypes = (
  careType: TripRequestCareTypeEnum
): VehicleBodyTypeEnum[] =>
  TRIP_REQUEST_CARE_TYPE_TO_VEHICLE_BODY_TYPES[careType] ?? []

export const getMatchingVehicleBodyTypeValues = (
  careType: TripRequestCareTypeEnum
): number[] => getMatchingVehicleBodyTypes(careType).map((t) => t as number)

const VEHICLE_BODY_TYPE_TO_TRIP_REQUEST_CARE_TYPE: Record<
  number,
  TripRequestCareTypeEnum
> = {
  [VehicleBodyTypeEnum.BUS]: TripRequestCareTypeEnum.BUS,
  [VehicleBodyTypeEnum.HATCHBACK]: TripRequestCareTypeEnum.CAR,
  [VehicleBodyTypeEnum.MICROBUS]: TripRequestCareTypeEnum.MICROBUS,
  [VehicleBodyTypeEnum.MINIBUS]: TripRequestCareTypeEnum.MINIBUS,
  [VehicleBodyTypeEnum.MINIVAN]: TripRequestCareTypeEnum.CAR,
  [VehicleBodyTypeEnum.OTHER]: TripRequestCareTypeEnum.CAR,
  [VehicleBodyTypeEnum.SEDAN]: TripRequestCareTypeEnum.CAR,
  [VehicleBodyTypeEnum.SUV]: TripRequestCareTypeEnum.CAR,
  [VehicleBodyTypeEnum.VAN]: TripRequestCareTypeEnum.CAR
}

export const vehicleBodyTypeToCareType = (
  bodyType: number | VehicleBodyTypeEnum
): TripRequestCareTypeEnum | undefined =>
  VEHICLE_BODY_TYPE_TO_TRIP_REQUEST_CARE_TYPE[bodyType]
